import React, { useState, useEffect } from "react";
import { Input, Form, Col, Row, Button, Select, Tooltip } from "antd";
import { PlusOutlined, DownloadOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import config from "util/ApiforContext";
import {
  defaultValidateMessages,
  onInputMaxLengthCheck,
  onKeyDownNumberOnly,
} from "./Helper";
import TextArea from "antd/lib/input/TextArea";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const METHOD_TYPE_MAP = {
  sftp: "FTP",
  mail: "EMAIL",
  webService: "WEBSERVICE",
  pdf: "PDF",
};

const TosBankInfoUpdateForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [activeBankId, setActiveBankId] = useState();

  const [connectionType, setConnectionType] = useState("none");
  const [requirements, setRequirements] = useState([]);
  const [activeRequirement, setActiveRequirement] = useState(null);

  const [isSupportwebService, setIsSupportwebService] = useState(false);
  const [isSupportSftp, setIsSupportSftp] = useState(false);
  const [isSupportMail, setIsSupportMail] = useState(false);
  const [isSupportPdf, setIsSupportPdf] = useState(false);

  useEffect(() => {
    if (props.data) {
      let initialConnectionType = "sftp";
      if (props.data.isUseEmail) {
        initialConnectionType = "mail";
      } else if (props.data.isUseWebService) {
        initialConnectionType = "webService";
      } else if (props.data.isUsePdf) {
        initialConnectionType = "pdf";
      }

      props.data.connectionType = initialConnectionType;
      form.setFieldsValue(props.data);
      setActiveBankId(props.data.bankId);
      setConnectionType(initialConnectionType);

      if (props.data.bankId) {
        RequestHandler.get(`/Bank/GetById/${props.data.bankId}`).then(
          (response) => {
            setIsSupportSftp(response.isTosSupport);
            setIsSupportwebService(response.isOnlineTosSupport);
            setIsSupportMail(response.isMailTosSupport);
            setIsSupportPdf(response.isTosSupportPdf);
          }
        );

        RequestHandler.get(
          `/TosBankInfo/GetBankRequirements/${props.data.bankId}`
        ).then((response) => {
          const reqs = response || [];
          setRequirements(reqs);

          const methodType = METHOD_TYPE_MAP[initialConnectionType];
          const req = reqs.find((r) => r.methodType === methodType);
          setActiveRequirement(req || null);
        });
      }
    }
  }, [props.data]);

  const onFinish = (values) => {
    setLoading(true);
    if (values.connectionType === "mail") {
      values.IsUseEmail = true;
    }

    if (values.connectionType === "webService") {
      values.IsUseWebService = true;
    }

    if (values.connectionType === "pdf") {
      values.IsUsePdf = true;
    }

    const methodType = METHOD_TYPE_MAP[values.connectionType];
    const req = requirements.find((r) => r.methodType === methodType);

    if (req) {
      if (!req.isClientIdRequired) delete values.clientId;
      if (!req.isClientSecretRequired) delete values.clientSecret;
      if (!req.isClientCertificateRequired) delete values.clientCertificate;
      if (!req.isCorporateCodeRequired) delete values.corporateCode;
      if (!req.isCustomerNumberRequired) delete values.customerNumber;
      if (!req.isHostRequired) delete values.host;
      if (!req.isPortRequired) delete values.port;
      if (!req.isDownloadFolderRequired) delete values.downloadFolder;
      if (!req.isUploadFolderRequired) delete values.uploadFolder;
      if (!req.isUserNameRequired) delete values.userName;
      if (!req.isPasswordRequired) delete values.password;
      if (!req.isRecipientEmailRequired) delete values.emailAddresses;
    }

    delete values.connectionType;

    RequestHandler.put(`/TosBankInfo`, values)
      .then((response) => {
        setLoading(false);
        if (response.success) {
          form.resetFields();
          setConnectionType("none");
          setActiveRequirement(null);

          if (props.onSuccess) props.onSuccess();
        }
      })
      .catch(function (error) {
        setLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
  };

  const onConnectionTypeChange = (type) => {
    const methodType = METHOD_TYPE_MAP[type];
    const req = requirements.find((r) => r.methodType === methodType);
    setActiveRequirement(req || null);
    setConnectionType(type);
  };

  const downloadKeyFile = () => {
    window.open(
      `${config.apiUrl}/TenantKeyFile/DownloadPublicKey/TosOnline/${activeBankId}?t=${RequestHandler.getToken()}`
    );
  };

  const FormItemLabelWithTooltip = ({ label, tooltip }) => {
    return (
      <>
        {label}{" "}
        <Tooltip
          title={
            <span>
              {tooltip
                ?.split("*")
                .filter((part) => part.trim() !== "")
                .map((line, index) => (
                  <div key={index}> * {line}</div>
                ))}
            </span>
          }
          overlayStyle={{ maxWidth: "450px" }}
        >
          <QuestionCircleOutlined
            className="gx-text-blue"
            style={{ marginLeft: 5, marginRight: 3, fontSize: "120%" }}
          />
        </Tooltip>
      </>
    );
  };

  const req = activeRequirement;

  return (
    <Form
      {...formItemLayout}
      validateMessages={defaultValidateMessages}
      form={form}
      name="tosBankInfoUpdate"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Row>
        <Col span={24}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="bankId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tanım Adı"
            rules={[{ required: true, whitespace: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="connectionType" label="Bağlantı Tipi">
            <Select placeholder="Seçiniz" onChange={onConnectionTypeChange}>
              {isSupportSftp ? (
                <Select.Option key="sftp" value="sftp">
                  Sftp
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportMail ? (
                <Select.Option key="mail" value="mail">
                  Mail
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportwebService ? (
                <Select.Option key="webService" value="webService">
                  Web Servis
                </Select.Option>
              ) : (
                <></>
              )}
              {isSupportPdf ? (
                <Select.Option key="pdf" value="pdf">
                  Pdf
                </Select.Option>
              ) : (
                <></>
              )}
            </Select>
          </Form.Item>

          {req && (
            <>
              {req.isCustomerNumberRequired && (
                <Form.Item
                  label={req.customerNumberLabel || "Müşteri Numarası"}
                  name="customerNumber"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.customerNumberLabel || "Müşteri Numarası"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input maxLength="125" onInput={onInputMaxLengthCheck} />
                </Form.Item>
              )}

              {req.isCorporateCodeRequired && (
                <Form.Item
                  label={req.corporateCodeLabel || "Kurumsal Kod"}
                  name="corporateCode"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.corporateCodeLabel || "Kurumsal Kod"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isHostRequired && req.isPortRequired && (
                <Form.Item style={{ marginBottom: 0 }} label="Host" required>
                  <Input.Group>
                    <Row gutter={2}>
                      <Col span={18}>
                        <Form.Item
                          name="host"
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "'Host' alanı gerekli.",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name="port"
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "'Port' alanı gerekli.",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Port"
                            onKeyDown={(e) => onKeyDownNumberOnly(e)}
                            maxLength="5"
                            onInput={onInputMaxLengthCheck}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Input.Group>
                </Form.Item>
              )}

              {req.isDownloadFolderRequired && (
                <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label="Gelen Dosya Yolu"
                      tooltip="destek@finekra.com mail adresine sorarak öğreniniz."
                    />
                  }
                  name="downloadFolder"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isUploadFolderRequired && (
                <Form.Item
                  label={
                    <FormItemLabelWithTooltip
                      label="Giden Dosya Yolu"
                      tooltip="destek@finekra.com mail adresine sorarak öğreniniz."
                    />
                  }
                  name="uploadFolder"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isUserNameRequired && (
                <Form.Item
                  label={req.userNameLabel || "Kullanıcı Adı"}
                  name="userName"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.userNameLabel || "Kullanıcı Adı"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isPasswordRequired && (
                <Form.Item
                  label={req.passwordLabel || "Şifre"}
                  name="password"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.passwordLabel || "Şifre"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              )}

              {req.isClientIdRequired && (
                <Form.Item
                  label={req.clientIdLabel || "Client Id"}
                  name="clientId"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.clientIdLabel || "Client Id"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isClientSecretRequired && (
                <Form.Item
                  label={req.clientSecretLabel || "Client Secret"}
                  name="clientSecret"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `'${req.clientSecretLabel || "Client Secret"}' alanı gerekli.`,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}

              {req.isClientCertificateRequired && (
                <Form.Item
                  label="Sertifika"
                  name="clientCertificate"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "'Sertifika' alanı gerekli.",
                    },
                  ]}
                >
                  <TextArea rows={10} />
                </Form.Item>
              )}

              {req.isKeyRequired && (
                <Form.Item label="Sertifika Dosyası">
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={downloadKeyFile}
                  >
                    Dosyayı indir
                  </Button>
                </Form.Item>
              )}

              {req.isRecipientEmailRequired && (
                <Form.Item
                  label="Alıcı E-postaları"
                  name="emailAddresses"
                  rules={[
                    {
                      validator: (rule, values) => {
                        if (!values || values.length === 0) {
                          return Promise.reject(
                            new Error(
                              "Alıcı e-posta adresleri boş bırakılamaz."
                            )
                          );
                        }

                        const emailRegex =
                          /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,6})$/;
                        const invalidInputs = values.filter(
                          (value) => !value.match(emailRegex)
                        );
                        if (invalidInputs.length === 0) {
                          return Promise.resolve();
                        }

                        if (invalidInputs.length === 1) {
                          return Promise.reject(
                            new Error(
                              `'${invalidInputs.join(
                                ""
                              )}' geçersiz bir e-postadır.`
                            )
                          );
                        } else {
                          return Promise.reject(
                            new Error(
                              `'${invalidInputs.join(
                                ", "
                              )}' değerleri geçersiz e-postadır.`
                            )
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    tokenSeparators={[",", ";"]}
                    mode="tags"
                    placeholder={
                      "Talimat girilen mail adreslerine gönderilir."
                    }
                  ></Select>
                </Form.Item>
              )}
            </>
          )}

          <Col style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              htmlType="submit"
              loading={loading}
            >
              Güncelle
            </Button>
          </Col>
        </Col>
      </Row>
    </Form>
  );
};
export default TosBankInfoUpdateForm;
